import { useState } from 'react';
import { View } from 'react-native';
import Svg, { Circle, Line, Polyline } from 'react-native-svg';

import { AppText } from '@/components/ui/app-text';
import { colors } from '@/theme/tokens';
import { currentMonthNamePl } from '@/utils/format';

/** Upcoming fixed cost drawn as a hollow labelled circle ON the plan line
 *  (mock poprawione_podsumowanie_linia) — current month only; past months get
 *  the bare line, because today's plan labels on an old chart would lie. */
export interface PlanMarker {
  day: number;
  label: string;
}

interface BudgetPaceChartProps {
  budgetGr: number;
  /** cumulative spending, one value per elapsed day of month (day 1..N) */
  cumulativeSpentGr: number[];
  daysInMonth: number;
  height?: number;
  /** Month name for the axis; defaults to the current month (Start card). */
  monthLabel?: string;
  /** Show the "Dziś" axis label — off when the chart shows a past day. */
  showTodayLabel?: boolean;
  /** Upcoming costs on the dashed plan line (current month only). */
  planMarkers?: PlanMarker[];
}

/**
 * "Czy idziemy po swoim budżecie?" — dashed line = the plan (budget spread
 * linearly over the month), solid line = actual cumulative spending.
 * Solid line turns warning-orange when spending runs ahead of the plan.
 */
export function BudgetPaceChart({
  budgetGr,
  cumulativeSpentGr,
  daysInMonth,
  height = 140,
  monthLabel,
  showTodayLabel = true,
  planMarkers = [],
}: BudgetPaceChartProps) {
  const [width, setWidth] = useState(0);

  const elapsed = cumulativeSpentGr.length;
  const spentGr = cumulativeSpentGr[elapsed - 1] ?? 0;
  const maxY = Math.max(budgetGr, spentGr) * 1.05 || 1;
  const padY = 6;

  const x = (day: number) => (day / daysInMonth) * width;
  const y = (v: number) => height - padY - (v / maxY) * (height - padY * 2);
  /** Height of the PLAN line at a given day (budget spread linearly). */
  const planY = (day: number) => y((budgetGr * day) / daysInMonth);

  const overPace = spentGr > (budgetGr * elapsed) / daysInMonth;
  const stroke = overPace ? colors.warning : colors.primary;

  const points = [`${x(0)},${y(0)}`, ...cumulativeSpentGr.map((v, i) => `${x(i + 1)},${y(v)}`)].join(' ');

  const monthShort = (monthLabel ?? currentMonthNamePl()).slice(0, 3); // Lipiec -> Lip
  const todayPct = Math.min(92, Math.max(8, (elapsed / daysInMonth) * 100));

  return (
    <View>
      <View style={{ height }} onLayout={(e) => setWidth(e.nativeEvent.layout.width)}>
        {width > 0 && (
          <Svg width={width} height={height}>
            {/* the plan: budget spent evenly across the month */}
            <Line
              x1={x(0)}
              y1={y(0)}
              x2={x(daysInMonth)}
              y2={y(budgetGr)}
              stroke={colors.outlineVariant}
              strokeWidth={2}
              strokeDasharray="6 6"
            />
            {/* upcoming costs sitting on the plan line */}
            {budgetGr > 0 &&
              planMarkers.map((m, i) => (
                <Circle
                  key={`pm-${i}`}
                  cx={x(m.day)}
                  cy={planY(m.day)}
                  r={5}
                  fill={colors.card}
                  stroke={colors.outlineVariant}
                  strokeWidth={2}
                />
              ))}
            <Polyline
              points={points}
              fill="none"
              stroke={stroke}
              strokeWidth={3}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <Circle cx={x(elapsed)} cy={y(spentGr)} r={5} fill={stroke} />
          </Svg>
        )}
      </View>
      {/* labels for the plan markers (RN text, not SVG — same font stack) */}
      {width > 0 && budgetGr > 0 && planMarkers.length > 0 && (
        <View pointerEvents="none" style={{ position: 'absolute', left: 0, top: 0, right: 0, height }}>
          {planMarkers.map((m, i) => (
            <AppText
              key={`pml-${i}`}
              variant="labelMd"
              color={colors.outline}
              numberOfLines={1}
              style={{
                position: 'absolute',
                left: Math.min(Math.max(x(m.day) - 40, 0), Math.max(width - 80, 0)),
                top: Math.max(planY(m.day) - 22, 0),
                width: 80,
                textAlign: 'center',
              }}
            >
              {m.label}
            </AppText>
          ))}
        </View>
      )}
      {/* axis: 1 <month> ... Dziś ... <last> <month> */}
      <View style={{ height: 18 }}>
        <AppText variant="labelMd" style={{ position: 'absolute', left: 0 }}>
          1 {monthShort}
        </AppText>
        {showTodayLabel && (
          <AppText
            variant="labelMd"
            color={colors.outline}
            style={{ position: 'absolute', left: `${todayPct}%`, transform: [{ translateX: '-50%' }] }}
          >
            Dziś
          </AppText>
        )}
        <AppText variant="labelMd" style={{ position: 'absolute', right: 0 }}>
          {daysInMonth} {monthShort}
        </AppText>
      </View>
    </View>
  );
}
